import {
	GROUP_ACTIVE_STATUS_LABEL_MAP,
	GROUP_ACTIVE_STATUS_LIST,
	HIERARCHY_LABEL_GROUP_VALUES,
	getAllowedChildLabels
} from 'app-config';
import { Column } from 'Components/VirtualTable/VirtualTable';
import { Input, Select, Button, Icon } from 'Components';
import styles from './GroupsHierarchy.module.scss';
import { useConfigs } from 'Hooks';
import { validateGroupName } from './GroupsHierarchy.helpers';
import { GroupActiveStatus } from 'Services/Api/Groups/Types';
import { HierarchyGroup } from './GroupsHierarchy.types';
import { useTranslation } from 'react-i18next';

interface GetColumnsParams {
	level: number;
	disabledIds: Record<string, string[]>;
	disableDeleteGroupButton: boolean;
	onAddGroupClick: () => void;
	onGroupNameChange: (value: string, fieldId: string) => void;
	onSyncCodeChange: (value: string, fieldId: string) => void;
	onHierarchyLabelChange: (
		value: string,
		id: string,
		level: string
	) => void;
	onDeleteGroupClick: (id: string) => void;
	isNewTenant?: boolean;
	parentIsProperty?: boolean;
	/** The hierarchy label of the parent group – used to restrict allowed child labels */
	parentHierarchyLabel?: string;
}

function useColumns({
	level,
	onGroupNameChange,
	onHierarchyLabelChange,
	disabledIds,
	disableDeleteGroupButton,
	onDeleteGroupClick,
	onAddGroupClick,
	onSyncCodeChange,
	isNewTenant,
	parentIsProperty,
	parentHierarchyLabel
}: GetColumnsParams) {
	const { t } = useTranslation();
	const { maxGroupNameCharacters } = useConfigs();
	const columns: Array<Column<HierarchyGroup>> = [
		{
			title: (
				<>
					{t('group.groupName')}
					&nbsp;
					{t('groups.columns.level', {
						level
					})}
				</>
			),
			renderComponent: (data, index) => {
				return data.isManageRow ? (
					<Button
						type="primary"
						shape="round"
						icon={<Icon type="plus" />}
						onMouseUp={onAddGroupClick}
					>
						{t('groups.groupDetails.groupDetailsActions.addGroup')}
					</Button>
				) : (
					<Input.HookFormInput
						name={`${data.parentId}.${index}.name`}
						className={styles.marginBottomNull}
						onBlur={(ev) => {
							onGroupNameChange(ev.target.value, data.id);
						}}
						required
						validate={validateGroupName(maxGroupNameCharacters)}
					/>
				);
			}
		},
		{
		title: t('Type'),
		className: styles.alignedStartTd,
		renderComponent: (data, index) => {
			// For new tenants (is_new=true): no default value
			// For old tenants (is_new=false) with isProperty=true: default to "Property"
			// For old tenants (is_new=false) with isProperty=false: no default value
			const hierarchyLabelDefault =
				!isNewTenant && parentIsProperty ? 'Property' : undefined;

			const allowedLabels = getAllowedChildLabels(parentHierarchyLabel);
			return data.isManageRow ? null : (
				<Select.HookFormSelect
					name={`${data.parentId}.${index}.hierarchyLabel`}
					required
					defaultValue={hierarchyLabelDefault}
					className={styles.marginBottomNull}
					onChange={(value: string) =>
						onHierarchyLabelChange(
							value,
							data.id,
							String(data.level)
						)
					}
				>
					{allowedLabels.map((label) => (
						<Select.Option key={label} value={label}>
							{t(`groups.groupDetails.groupLevelType.${label}`)}
						</Select.Option>
					))}
				</Select.HookFormSelect>
			);
		}
		},
		{
			title: t('group.syncCode'),
			renderComponent: (data, index) => {
				return data.isManageRow ? null : (
					<Input.HookFormInput
						name={`${data.parentId}.${index}.syncCode`}
						className={styles.marginBottomNull}
						onBlur={(ev) => {
							onSyncCodeChange(ev.target.value, data.id);
						}}
						minLength={1}
						maxLength={60}
					/>
				);
			}
		},
		{
			title: t('group.status'),
			renderComponent: (data, index) => {
				return data.isManageRow ? null : (
					<Select.HookFormSelect
						name={`${data.parentId}.${index}.active`}
						required
						defaultValue={GROUP_ACTIVE_STATUS_LABEL_MAP[
							GroupActiveStatus.Active
						]()}
						className={styles.marginBottomNull}
					>
						{GROUP_ACTIVE_STATUS_LIST.map((status) => (
							<Select.Option key={status} value={status}>
								{GROUP_ACTIVE_STATUS_LABEL_MAP[status]()}
							</Select.Option>
						))}
					</Select.HookFormSelect>
				);
			}
		},
		{
			title: t('actions'),
			width: 80,
			className: styles.centeredTd,
			renderComponent: (data) => {
				return data.isManageRow ? null : (
					<Button
						shape="round"
						type="primary"
						size="small"
						ghost
						icon={<Icon type="close" />}
						disabled={disableDeleteGroupButton}
						onMouseDown={() => onDeleteGroupClick(data.id)}
					/>
				);
			}
		}
	];

	return columns;
}

export default useColumns;
